/**
 * Modal dialogs on the native `<dialog>` element.
 *
 * `showModal()` gives the focus trap, the Escape key and the inert background
 * for free, and those are the three that are hard to get right by hand. Two
 * things it does NOT give, and this module adds:
 *
 *   1. **Light dismiss.** Clicking the backdrop closes nothing by default.
 *      Where `closedby` is supported the platform does it; elsewhere the
 *      pointer-position fallback below does, because "overlay click closes it"
 *      is a stated requirement of the limit dialog and not a nicety.
 *   2. **Focus return.** The spec returns focus to the invoker in current
 *      engines, but only when the invoker is still in the document and still
 *      focusable. A dialog opened from a button that the same interaction
 *      disabled — which is exactly the submit button's situation — lands focus
 *      on `<body>`, and the user is dumped at the top of the page.
 *
 * `body[data-modal-open]` is set while any dialog is open. That is what
 * suppresses the mobile anchor ad: an adhesion banner underneath a modal is a
 * Better Ads Standards problem and also just looks like a scam.
 */

import { data, on, qsa, setData } from './dom.ts';

const supportsClosedBy = (): boolean =>
  typeof HTMLDialogElement !== 'undefined' && 'closedBy' in HTMLDialogElement.prototype;

let openCount = 0;

function markOpen(delta: number): void {
  openCount = Math.max(0, openCount + delta);
  if (openCount > 0) setData(document.body, 'modal-open', 'true');
  else setData(document.body, 'modal-open', null);
}

export interface DialogHandle {
  readonly el: HTMLDialogElement;
  open: () => void;
  close: (reason?: string) => void;
  readonly isOpen: () => boolean;
}

export interface DialogOptions {
  /** Fires once per close, with 'button' | 'escape' | 'overlay' | 'api'. */
  onClose?: (reason: string) => void;
  onOpen?: () => void;
}

/**
 * Wire a `<dialog>` that is already in the page. Idempotent per element.
 */
export function attachDialog(el: HTMLDialogElement, options: DialogOptions = {}): DialogHandle {
  if (data(el, 'dialog-bound') === 'true') {
    return (el as HTMLDialogElement & { _handle?: DialogHandle })._handle as DialogHandle;
  }
  setData(el, 'dialog-bound', 'true');

  let invoker: HTMLElement | null = null;
  let reason = 'api';

  if (supportsClosedBy()) {
    el.setAttribute('closedby', 'any');
  } else {
    // Fallback light dismiss. The click target for a backdrop click is the
    // <dialog> itself, but so is a click on its own padding — so the pointer
    // position is compared against the content box rather than trusting the
    // target alone.
    on(el, 'click', (ev) => {
      const pointer = ev as MouseEvent;
      if (pointer.target !== el) return;
      const rect = el.getBoundingClientRect();
      const inside =
        pointer.clientX >= rect.left &&
        pointer.clientX <= rect.right &&
        pointer.clientY >= rect.top &&
        pointer.clientY <= rect.bottom;
      if (!inside) handle.close('overlay');
    });
  }

  // Escape (and native light dismiss under `closedby`) reach `cancel` before
  // `close`, which is how a user-driven close is told apart from ours without
  // a keydown listener. `programmatic` guards the case where our own
  // requestClose() raises the same event.
  let programmatic = false;
  on(el, 'cancel', () => {
    if (!programmatic) reason = 'dismiss';
  });

  on(el, 'close', () => {
    markOpen(-1);
    options.onClose?.(reason);
    reason = 'api';
    // Restore focus ourselves when the invoker can still take it.
    if (invoker?.isConnected && typeof invoker.focus === 'function') {
      invoker.focus({ preventScroll: true });
    }
    invoker = null;
  });

  for (const button of qsa<HTMLElement>('[data-dialog-close]', el)) {
    on(button, 'click', () => handle.close('button'));
  }

  const handle: DialogHandle = {
    el,
    isOpen: () => el.open,
    open() {
      if (el.open) return;
      const active = document.activeElement;
      invoker = active instanceof HTMLElement ? active : null;
      markOpen(1);
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
      options.onOpen?.();
    },
    close(why = 'api') {
      if (!el.open) return;
      reason = why;
      programmatic = true;
      queueMicrotask(() => {
        programmatic = false;
      });
      // requestClose() fires `cancel` before `close`, so a handler that wants
      // to veto a close gets the same event whether the user pressed Escape or
      // a button. close() skips cancel and makes the two paths differ.
      const dialog = el as HTMLDialogElement & { requestClose?: (returnValue?: string) => void };
      if (typeof dialog.requestClose === 'function') dialog.requestClose();
      else if (typeof el.close === 'function') el.close();
      else {
        el.removeAttribute('open');
        el.dispatchEvent(new Event('close'));
      }
    },
  };

  (el as HTMLDialogElement & { _handle?: DialogHandle })._handle = handle;
  return handle;
}
